import { useAuthStore } from "@/stores/authStore";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { UserRole } from "@/lib/types";

export function useCurrentOrgRole(): UserRole | null {
  const user = useAuthStore((s) => s.user);
  const selectedOrganizationId = useOrganizationsStore(
    (s) => s.selectedOrganizationId,
  );

  if (!user || !selectedOrganizationId) return null;
  const membership = user.organizations.find(
    (o) => o.id === selectedOrganizationId,
  );
  return membership?.role ?? null;
}

export function canAccessApcmOps(role: UserRole | null, isSuperuser: boolean) {
  if (isSuperuser) return true;
  if (role === null) return false;
  return role !== "staff";
}

/**
 * Demo calls dial arbitrary phone numbers, so the real gate lives on the API
 * (superuser, or ADMIN/USER/STAFF in any organization). This is only about
 * whether to bother showing the entry point — the page renders a readable
 * message if the API turns the user away.
 *
 * Unlike APCM ops this deliberately includes STAFF: front-office staff are the
 * people who actually run prospect demos.
 */
export function canAccessDemoCalls(role: UserRole | null, isSuperuser: boolean) {
  if (isSuperuser) return true;
  return role !== null;
}

/**
 * Who may invite people into the currently selected organization.
 *
 * ADMIN only, matching the API's ORG_ADMIN_ROLES. This is what lets a clinic
 * add its own staff without a Pear superuser doing it by hand. The server
 * enforces it independently and scopes it to this specific org — an ADMIN of
 * one organization cannot touch another.
 */
export function canManageOrgUsers(role: UserRole | null, isSuperuser: boolean) {
  if (isSuperuser) return true;
  return role === "admin";
}
